// Secure Files V1 — chiffrement/déchiffrement 100 % navigateur (Web Crypto, AES-GCM 256).
// Module SÉPARÉ : ne modifie aucune crypto existante (secrets, SMS V3, Secure Request, générateur).
// Clé : 32 octets CSPRNG, exportée en base64url, placée UNIQUEMENT dans le fragment « #k= » du lien.
// Deux IV indépendants de 12 octets (fichier / manifest) + AAD distincts => même clé sans réutilisation de nonce.
import { b64urlEncode, b64urlDecode } from "../crypto";

export const SECURE_FILE_VERSION = "secure-file-v1";
export const KEY_BYTES = 32;
export const IV_BYTES = 12;
export const CONTEXT_ID_BYTES = 16;
const AAD_PREFIX = "securepass-file-v1";

const subtle = () => window.crypto.subtle;
const enc = (s) => new TextEncoder().encode(s);

export const fileAad = (ctx) => enc(`${AAD_PREFIX}|${ctx}|file`);
export const manifestAad = (ctx) => enc(`${AAD_PREFIX}|${ctx}|manifest`);

export function randomBytes(n) {
  const b = new Uint8Array(n);
  window.crypto.getRandomValues(b);
  return b;
}

const sameBytes = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// Deux IV distincts, tirés indépendamment (égalité interdite explicitement).
export function distinctIvs() {
  const fileIv = randomBytes(IV_BYTES);
  let manifestIv = randomBytes(IV_BYTES);
  while (sameBytes(fileIv, manifestIv)) manifestIv = randomBytes(IV_BYTES);
  return { fileIv, manifestIv };
}

export async function importFileKey(keyB64) {
  const raw = b64urlDecode(keyB64 || "");
  if (raw.length !== KEY_BYTES) throw new Error("invalid_key");
  return subtle().importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
}

// Chiffre fichier + manifest {version,name,mime,size}. Retourne le ciphertext (Uint8Array), les champs
// réseau (aucun nom/MIME/clé) et la clé base64url destinée au seul fragment d'URL.
export async function encryptSecureFile(bytes, { name, mime }) {
  const plain = new Uint8Array(bytes);
  if (plain.length === 0) throw new Error("empty_file");
  const rawKey = randomBytes(KEY_BYTES);
  const key = await subtle().importKey("raw", rawKey, { name: "AES-GCM" }, false, ["encrypt"]);
  const ctx = b64urlEncode(randomBytes(CONTEXT_ID_BYTES));
  const { fileIv, manifestIv } = distinctIvs();
  const manifest = enc(JSON.stringify({ version: 1, name: String(name || ""), mime: String(mime || ""), size: plain.length }));
  const ct = await subtle().encrypt({ name: "AES-GCM", iv: fileIv, additionalData: fileAad(ctx) }, key, plain);
  const mct = await subtle().encrypt({ name: "AES-GCM", iv: manifestIv, additionalData: manifestAad(ctx) }, key, manifest);
  return {
    ciphertext: new Uint8Array(ct),
    fields: {
      manifest_ciphertext: b64urlEncode(mct),
      manifest_iv: b64urlEncode(manifestIv),
      file_iv: b64urlEncode(fileIv),
      crypto_context_id: ctx,
      crypto_version: SECURE_FILE_VERSION,
    },
    key: b64urlEncode(rawKey),
  };
}

// Déchiffre le manifest AVANT toute consommation : une mauvaise clé ne brûle jamais le fichier.
export async function decryptManifest(meta, keyB64) {
  const key = await importFileKey(keyB64);
  const pt = await subtle().decrypt(
    { name: "AES-GCM", iv: b64urlDecode(meta.manifest_iv), additionalData: manifestAad(meta.crypto_context_id) },
    key, b64urlDecode(meta.manifest_ciphertext));
  const m = JSON.parse(new TextDecoder().decode(pt));
  if (m.version !== 1 || typeof m.name !== "string" || typeof m.mime !== "string" || !Number.isInteger(m.size)) {
    throw new Error("invalid_manifest");
  }
  return m;
}

export async function decryptSecureFile(meta, ciphertext, keyB64) {
  const key = await importFileKey(keyB64);
  const pt = await subtle().decrypt(
    { name: "AES-GCM", iv: b64urlDecode(meta.file_iv), additionalData: fileAad(meta.crypto_context_id) },
    key, new Uint8Array(ciphertext));
  return new Uint8Array(pt);
}

// Corps multipart : UNIQUEMENT le chiffré + champs techniques. Nom générique, type opaque.
export function buildUploadForm({ ciphertext, fields }, expiration) {
  const fd = new FormData();
  fd.append("encrypted_file", new Blob([ciphertext], { type: "application/octet-stream" }), "encrypted.bin");
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  fd.append("expiration", expiration);
  return fd;
}
